/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { DatacenterConnection } from "../DatacenterConnectionsData";
import {
  getHardwareByDatacenterId,
  getPortsByHardwareId,
  getHardwareById,
  getPortId,
  Hardware,
} from "../../Hardware/request/request";
import { getHardwareListByDataCenter } from "../../Kontroledge/Port/request/request";
import {
  getProviders,
  getAllDatacenters,
  getDatacenterById,
} from "../../DataCenter/request/request";

export interface DatacenterConnectionsCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (newConnection: Partial<DatacenterConnection>) => Promise<void>;
}

export const DatacenterConnectionsCreateModal: React.FC<
  DatacenterConnectionsCreateModalProps
> = ({ isOpen, onClose, onCreate }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // Form state
  const [formData, setFormData] = useState<Partial<DatacenterConnection>>({
    subId: "",
    datacenterIdSource: "",
    hardwareIdSource: null,
    portIdSource: null,
    capacityMbps: 0,
    connectionType: "",
    datacenterIdDest: undefined,
    hardwareIdDest: null,
    portIdDest: null,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Tab states
  const [activeSourceTab, setActiveSourceTab] = useState<
    "datacenter" | "hardware" | "port"
  >("datacenter");
  const [activeDestTab, setActiveDestTab] = useState<
    "datacenter" | "hardware" | "port"
  >("datacenter");

  // Source dropdowns data
  const [sourceDataCenters, setSourceDataCenters] = useState<any[]>([]);
  const [isLoadingSourceDataCenters, setIsLoadingSourceDataCenters] =
    useState(false);
  const [
    isLoadingSourceDatacenterDetails,
    setIsLoadingSourceDatacenterDetails,
  ] = useState(false);
  const [selectedSourceDatacenter, setSelectedSourceDatacenter] = useState<
    any | null
  >(null);
  const [selectedSourceDatacenterLabel, setSelectedSourceDatacenterLabel] =
    useState<string>("");

  // Source hardware data
  const [sourceHardwareList, setSourceHardwareList] = useState<any[]>([]);
  const [isLoadingSourceHardwareList, setIsLoadingSourceHardwareList] =
    useState(false);
  const [isLoadingSourceHardwareDetails, setIsLoadingSourceHardwareDetails] =
    useState(false);
  const [selectedSourceHardwareDetails, setSelectedSourceHardwareDetails] =
    useState<any | null>(null);
  const [selectedSourceHardwareLabel, setSelectedSourceHardwareLabel] =
    useState<string>("");

  // Source port data
  const [sourcePortsList, setSourcePortsList] = useState<any[]>([]);
  const [isLoadingSourcePortsList, setIsLoadingSourcePortsList] =
    useState(false);
  const [isLoadingSourcePortDetails, setIsLoadingSourcePortDetails] =
    useState(false);
  const [selectedSourcePortDetails, setSelectedSourcePortDetails] = useState<
    any | null
  >(null);
  const [selectedSourcePortLabel, setSelectedSourcePortLabel] =
    useState<string>("");

  // Destination dropdowns data
  const [dataCenters, setDataCenters] = useState<any[]>([]);
  const [hardwareListForDest, setHardwareListForDest] = useState<any[]>([]);
  const [destPorts, setDestPorts] = useState<any[]>([]);
  const [isLoadingDataCenters, setIsLoadingDataCenters] = useState(false);
  const [isLoadingDestPorts, setIsLoadingDestPorts] = useState(false);
  const [isLoadingHardwareList, setIsLoadingHardwareList] = useState(false);
  const [isLoadingHardwareDetails, setIsLoadingHardwareDetails] =
    useState(false);
  const [isLoadingDestDatacenterDetails, setIsLoadingDestDatacenterDetails] =
    useState(false);
  const [selectedDestDatacenter, setSelectedDestDatacenter] = useState<
    any | null
  >(null);
  const [selectedDestHardwareDetails, setSelectedDestHardwareDetails] =
    useState<any | null>(null);
  const [selectedDestPortDetails, setSelectedDestPortDetails] = useState<
    any | null
  >(null);

  // Destination selections (for dropdowns)
  const [selectedDestDatacenterLabel, setSelectedDestDatacenterLabel] =
    useState<string>("");
  const [selectedDestHardwareLabel, setSelectedDestHardwareLabel] =
    useState<string>("");
  const [selectedDestPortLabel, setSelectedDestPortLabel] =
    useState<string>("");

  // Providers data
  const [providers, setProviders] = useState<
    Array<{ id: number; name: string; idAddon: string }>
  >([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [selectedProviderLabel, setSelectedProviderLabel] =
    useState<string>("");

  // Handle destination tab change
  const handleDestTabChange = (tab: "datacenter" | "hardware" | "port") => {
    setActiveDestTab(tab);
  };

  // Helper function to extract API data
  const extractApiData = (resp: any): any => {
    return (resp as any)?.data?.data ?? (resp as any)?.data;
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        subId: "",
        datacenterIdSource: "",
        hardwareIdSource: null,
        portIdSource: null,
        capacityMbps: 0,
        connectionType: "",
        datacenterIdDest: undefined,
        hardwareIdDest: null,
        portIdDest: null,
      });
      setErrorMessage(null);
      setActiveSourceTab("datacenter");
      setActiveDestTab("datacenter");
      // Reset all selections
      setSelectedSourceDatacenterLabel("");
      setSelectedSourceDatacenter(null);
      setSelectedSourceHardwareLabel("");
      setSelectedSourceHardwareDetails(null);
      setSelectedSourcePortLabel("");
      setSelectedSourcePortDetails(null);
      setSelectedDestDatacenterLabel("");
      setSelectedDestDatacenter(null);
      setSelectedDestHardwareLabel("");
      setSelectedDestHardwareDetails(null);
      setSelectedDestPortLabel("");
      setSelectedDestPortDetails(null);
      setSourceHardwareList([]);
      setSourcePortsList([]);
      setHardwareListForDest([]);
      setDestPorts([]);
      // Reset tracking IDs
      setLastSourceDatacenterId(null);
      setLastDestDatacenterId(null);
      setLastSourceHardwareId(null);
      setLastDestHardwareId(null);
    }
  }, [isOpen]);

  // Fetch datacenters and providers when modal opens
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      // Fetch datacenters for Destination
      (async () => {
        try {
          setIsLoadingDataCenters(true);
          const token = await getAccessTokenSilently();
          const resp = await getAllDatacenters(token);
          const dcRaw = extractApiData(resp) ?? [];
          setDataCenters(dcRaw);
        } catch (error) {
          console.error("Error fetching datacenters:", error);
          setDataCenters([]);
        } finally {
          setIsLoadingDataCenters(false);
        }
      })();

      // Fetch datacenters for Source
      (async () => {
        try {
          setIsLoadingSourceDataCenters(true);
          const token = await getAccessTokenSilently();
          const resp = await getAllDatacenters(token);
          const dcRaw = extractApiData(resp) ?? [];
          setSourceDataCenters(dcRaw);
        } catch (error) {
          console.error("Error fetching source datacenters:", error);
          setSourceDataCenters([]);
        } finally {
          setIsLoadingSourceDataCenters(false);
        }
      })();

      (async () => {
        try {
          setIsLoadingProviders(true);
          const token = await getAccessTokenSilently();
          const resp = await getProviders(token);
          const providersData = extractApiData(resp) ?? [];
          setProviders(providersData);
        } catch (error) {
          console.error("Error fetching providers:", error);
          setProviders([]);
        } finally {
          setIsLoadingProviders(false);
        }
      })();
    }
  }, [isOpen, isAuthenticated, getAccessTokenSilently]);

  // Track last fetched datacenter ID for Source to avoid unnecessary reloads
  const [lastSourceDatacenterId, setLastSourceDatacenterId] = useState<
    string | null
  >(null);

  // Fetch hardware list for Source when in hardware or port tab and datacenter is selected
  useEffect(() => {
    if (
      !isOpen ||
      !isAuthenticated ||
      (activeSourceTab !== "hardware" && activeSourceTab !== "port")
    ) {
      // Don't reset data when switching tabs - keep it for persistence
      return;
    }

    const datacenterId =
      selectedSourceDatacenter?.datacenterId || formData.datacenterIdSource;

    if (!datacenterId) {
      // Only clear if datacenter is not selected
      if (!selectedSourceDatacenter && !formData.datacenterIdSource) {
        setSourceHardwareList([]);
        setLastSourceDatacenterId(null);
      }
      return;
    }

    // Only fetch if datacenter changed (not if already fetched for this ID)
    if (lastSourceDatacenterId === datacenterId) {
      return;
    }

    (async () => {
      try {
        setIsLoadingSourceHardwareList(true);
        const token = await getAccessTokenSilently();
        const resp = await getHardwareListByDataCenter(token, datacenterId);
        const hwRaw = extractApiData(resp) ?? [];
        setSourceHardwareList(hwRaw);
        setLastSourceDatacenterId(datacenterId);
      } catch (error) {
        console.error("Error fetching source hardware list:", error);
        setSourceHardwareList([]);
        setLastSourceDatacenterId(null);
      } finally {
        setIsLoadingSourceHardwareList(false);
      }
    })();
  }, [
    activeSourceTab,
    selectedSourceDatacenter,
    formData.datacenterIdSource,
    isOpen,
    isAuthenticated,
    getAccessTokenSilently,
    lastSourceDatacenterId,
  ]);

  // Track last fetched datacenter ID for Destination to avoid unnecessary reloads
  const [lastDestDatacenterId, setLastDestDatacenterId] = useState<
    string | null
  >(null);

  // Fetch hardware list for Destination when in hardware or port tab and datacenter is selected
  useEffect(() => {
    if (
      !isOpen ||
      !isAuthenticated ||
      (activeDestTab !== "hardware" && activeDestTab !== "port")
    ) {
      // Don't reset data when switching tabs - keep it for persistence
      return;
    }

    const datacenterId =
      selectedDestDatacenter?.datacenterId || formData.datacenterIdDest;

    if (!datacenterId) {
      // Only clear if datacenter is not selected
      if (!selectedDestDatacenter && !formData.datacenterIdDest) {
        setHardwareListForDest([]);
        setLastDestDatacenterId(null);
      }
      return;
    }

    // Only fetch if datacenter changed (not if already fetched for this ID)
    if (lastDestDatacenterId === datacenterId) {
      return;
    }

    (async () => {
      try {
        setIsLoadingHardwareList(true);
        const token = await getAccessTokenSilently();
        const resp = await getHardwareListByDataCenter(token, datacenterId);
        const hwRaw = extractApiData(resp) ?? [];
        setHardwareListForDest(hwRaw);
        setLastDestDatacenterId(datacenterId);
      } catch (error) {
        console.error("Error fetching destination hardware list:", error);
        setHardwareListForDest([]);
        setLastDestDatacenterId(null);
      } finally {
        setIsLoadingHardwareList(false);
      }
    })();
  }, [
    activeDestTab,
    selectedDestDatacenter,
    formData.datacenterIdDest,
    isOpen,
    isAuthenticated,
    getAccessTokenSilently,
    lastDestDatacenterId,
  ]);

  // Track last fetched hardware ID for Source ports to avoid unnecessary reloads
  const [lastSourceHardwareId, setLastSourceHardwareId] = useState<
    number | null
  >(null);

  // Fetch ports list for Source when in port tab and hardware is selected
  useEffect(() => {
    if (!isOpen || !isAuthenticated || activeSourceTab !== "port") {
      // Don't reset data when switching tabs - keep it for persistence
      return;
    }

    const hardwareId =
      selectedSourceHardwareDetails?.id || formData.hardwareIdSource;

    if (!hardwareId) {
      // Only clear if hardware is not selected
      if (!selectedSourceHardwareDetails && !formData.hardwareIdSource) {
        setSourcePortsList([]);
        setLastSourceHardwareId(null);
      }
      return;
    }

    // Only fetch if hardware changed (not if already fetched for this ID)
    if (lastSourceHardwareId === hardwareId) {
      return;
    }

    (async () => {
      try {
        setIsLoadingSourcePortsList(true);
        const token = await getAccessTokenSilently();
        const resp = await getPortsByHardwareId(token, String(hardwareId));
        const portData = extractApiData(resp);
        const portsArray = portData?.ports || [];
        setSourcePortsList(portsArray);
        setLastSourceHardwareId(hardwareId);
      } catch (error) {
        console.error("Error fetching source ports list:", error);
        setSourcePortsList([]);
        setLastSourceHardwareId(null);
      } finally {
        setIsLoadingSourcePortsList(false);
      }
    })();
  }, [
    activeSourceTab,
    selectedSourceHardwareDetails,
    formData.hardwareIdSource,
    isOpen,
    isAuthenticated,
    getAccessTokenSilently,
    lastSourceHardwareId,
  ]);

  // Track last fetched hardware ID for Destination ports to avoid unnecessary reloads
  const [lastDestHardwareId, setLastDestHardwareId] = useState<number | null>(
    null
  );

  // Fetch ports list for Destination when in port tab and hardware is selected
  useEffect(() => {
    if (!isOpen || !isAuthenticated || activeDestTab !== "port") {
      // Don't reset data when switching tabs - keep it for persistence
      return;
    }

    const hardwareId =
      selectedDestHardwareDetails?.id || formData.hardwareIdDest;

    if (!hardwareId) {
      // Only clear if hardware is not selected
      if (!selectedDestHardwareDetails && !formData.hardwareIdDest) {
        setDestPorts([]);
        setLastDestHardwareId(null);
      }
      return;
    }

    // Only fetch if hardware changed (not if already fetched for this ID)
    if (lastDestHardwareId === hardwareId) {
      return;
    }

    (async () => {
      try {
        setIsLoadingDestPorts(true);
        const token = await getAccessTokenSilently();
        const resp = await getPortsByHardwareId(token, String(hardwareId));
        const portData = extractApiData(resp);
        const portsArray = portData?.ports || [];
        setDestPorts(portsArray);
        setLastDestHardwareId(hardwareId);
      } catch (error) {
        console.error("Error fetching destination ports list:", error);
        setDestPorts([]);
        setLastDestHardwareId(null);
      } finally {
        setIsLoadingDestPorts(false);
      }
    })();
  }, [
    activeDestTab,
    selectedDestHardwareDetails,
    formData.hardwareIdDest,
    isOpen,
    isAuthenticated,
    getAccessTokenSilently,
    lastDestHardwareId,
  ]);

  const handleInputChange = (
    field: keyof DatacenterConnection,
    value: string | number | null | undefined
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value === undefined ? undefined : value,
    }));
  };

  // Handle source datacenter selection
  const handleSourceDatacenterSelect = async (displayName: string) => {
    if (!displayName) {
      setSelectedSourceDatacenterLabel("");
      setSelectedSourceDatacenter(null);
      handleInputChange("datacenterIdSource", "");
      // Reset hardware and port in ALL tabs when datacenter is cleared
      setSelectedSourceHardwareLabel("");
      setSelectedSourceHardwareDetails(null);
      setSelectedSourcePortLabel("");
      setSelectedSourcePortDetails(null);
      setSourceHardwareList([]);
      setSourcePortsList([]);
      handleInputChange("hardwareIdSource", null);
      handleInputChange("portIdSource", null);
      return;
    }

    setSelectedSourceDatacenterLabel(displayName);
    const datacenter = sourceDataCenters.find(
      (dc) => dc.displayName === displayName
    );
    if (!datacenter) return;

    try {
      setIsLoadingSourceDatacenterDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getDatacenterById(token, datacenter.datacenterId);
      const datacenterDetails = extractApiData(resp);

      if (datacenterDetails) {
        setSelectedSourceDatacenter(datacenterDetails);
        handleInputChange("datacenterIdSource", datacenterDetails.datacenterId);

        // Reset hardware and port in ALL tabs when datacenter changes
        setSelectedSourceHardwareLabel("");
        setSelectedSourceHardwareDetails(null);
        setSelectedSourcePortLabel("");
        setSelectedSourcePortDetails(null);
        setSourceHardwareList([]);
        setSourcePortsList([]);
        setLastSourceHardwareId(null);
        handleInputChange("hardwareIdSource", null);
        handleInputChange("portIdSource", null);
      }
    } catch (error) {
      console.error("Error fetching source datacenter details:", error);
    } finally {
      setIsLoadingSourceDatacenterDetails(false);
    }
  };

  // Handle source hardware selection
  const handleSourceHardwareSelect = async (hardwareOption: string) => {
    setSelectedSourceHardwareLabel(hardwareOption);

    const parts = hardwareOption.split(" - ");
    const hostname = parts[0];
    const serialNumber = parts[1];
    const hardware = sourceHardwareList.find(
      (hw) => hw.hostname === hostname && hw.serialNumber === serialNumber
    );

    if (!hardware) return;

    try {
      setIsLoadingSourceHardwareDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getHardwareById(token, String(hardware.id));
      const hardwareDetails = extractApiData(resp);

      if (hardwareDetails) {
        setSelectedSourceHardwareDetails(hardwareDetails);
        handleInputChange("hardwareIdSource", hardware.id);

        // Reset port in ALL tabs when hardware changes
        setSelectedSourcePortLabel("");
        setSelectedSourcePortDetails(null);
        setSourcePortsList([]);
        setLastSourceHardwareId(null);
        handleInputChange("portIdSource", null);
      }
    } catch (error) {
      console.error("Error fetching source hardware details:", error);
    } finally {
      setIsLoadingSourceHardwareDetails(false);
    }
  };

  // Handle source port selection
  const handleSourcePortSelect = async (portNumber: string) => {
    setSelectedSourcePortLabel(portNumber);
    const port = sourcePortsList.find((p) => p.portNumber === portNumber);

    if (!port) return;

    try {
      setIsLoadingSourcePortDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getPortId(token, String(port.id));
      const portDetails = extractApiData(resp);

      if (portDetails) {
        setSelectedSourcePortDetails(portDetails);
        handleInputChange("portIdSource", port.id);
      }
    } catch (error) {
      console.error("Error fetching source port details:", error);
    } finally {
      setIsLoadingSourcePortDetails(false);
    }
  };

  // Handle destination datacenter selection
  const handleDestDatacenterSelect = async (displayName: string) => {
    if (displayName === "Select All" || !displayName) {
      setSelectedDestDatacenterLabel("");
      setSelectedDestDatacenter(null);
      handleInputChange("datacenterIdDest", undefined);
      // Reset hardware and port in ALL tabs when datacenter is cleared
      setSelectedDestHardwareLabel("");
      setSelectedDestHardwareDetails(null);
      setSelectedDestPortLabel("");
      setHardwareListForDest([]);
      setDestPorts([]);
      handleInputChange("hardwareIdDest", null);
      handleInputChange("portIdDest", null);
      return;
    }

    setSelectedDestDatacenterLabel(displayName);
    const datacenter = dataCenters.find((dc) => dc.displayName === displayName);
    if (!datacenter) return;

    try {
      setIsLoadingDestDatacenterDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getDatacenterById(token, datacenter.datacenterId);
      const datacenterDetails = extractApiData(resp);

      if (datacenterDetails) {
        setSelectedDestDatacenter(datacenterDetails);
        handleInputChange("datacenterIdDest", datacenterDetails.datacenterId);

        // Reset hardware and port in ALL tabs when datacenter changes
        setSelectedDestHardwareLabel("");
        setSelectedDestHardwareDetails(null);
        setSelectedDestPortLabel("");
        setSelectedDestPortDetails(null);
        setHardwareListForDest([]);
        setDestPorts([]);
        setLastDestHardwareId(null);
        handleInputChange("hardwareIdDest", null);
        handleInputChange("portIdDest", null);
      }
    } catch (error) {
      console.error("Error fetching destination datacenter details:", error);
    } finally {
      setIsLoadingDestDatacenterDetails(false);
    }
  };

  // Handle destination hardware selection (for hardware tab)
  const handleDestHardwareSelectForTab = async (hardwareOption: string) => {
    setSelectedDestHardwareLabel(hardwareOption);

    const parts = hardwareOption.split(" - ");
    const hostname = parts[0];
    const serialNumber = parts[1];
    const hardware = hardwareListForDest.find(
      (hw) => hw.hostname === hostname && hw.serialNumber === serialNumber
    );

    if (!hardware) return;

    try {
      setIsLoadingHardwareDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getHardwareById(token, String(hardware.id));
      const hardwareDetails = extractApiData(resp);

      if (hardwareDetails) {
        setSelectedDestHardwareDetails(hardwareDetails);
        handleInputChange("hardwareIdDest", hardware.id);

        // Reset port in ALL tabs when hardware changes
        setSelectedDestPortLabel("");
        setSelectedDestPortDetails(null);
        setDestPorts([]);
        setLastDestHardwareId(null);
        handleInputChange("portIdDest", null);
      }
    } catch (error) {
      console.error("Error fetching destination hardware details:", error);
    } finally {
      setIsLoadingHardwareDetails(false);
    }
  };

  // Handle destination port selection (for port tab)
  const handleDestPortSelectForTab = async (portNumber: string) => {
    setSelectedDestPortLabel(portNumber);
    const port = destPorts.find((p) => p.portNumber === portNumber);

    if (!port) return;

    try {
      setIsLoadingDestPorts(true);
      const token = await getAccessTokenSilently();
      const resp = await getPortId(token, String(port.id));
      const portDetails = extractApiData(resp);

      if (portDetails) {
        setSelectedDestPortDetails(portDetails);
        handleInputChange("portIdDest", port.id);
      }
    } catch (error) {
      console.error("Error fetching destination port details:", error);
    } finally {
      setIsLoadingDestPorts(false);
    }
  };

  // Get dropdown options
  const sourceDataCenterOptions = sourceDataCenters
    .map((dc) => dc.displayName)
    .sort();
  const dataCenterOptions = [
    "Select All",
    ...dataCenters.map((dc) => dc.displayName).sort(),
  ];
  const sourceHardwareOptions = sourceHardwareList
    .map((hw) => `${hw.hostname} - ${hw.serialNumber}`)
    .filter(Boolean)
    .sort();
  const hardwareOptionsForDest = hardwareListForDest
    .map((hw) => `${hw.hostname} - ${hw.serialNumber}`)
    .filter(Boolean)
    .sort();
  const sourcePortOptions = sourcePortsList
    .map((p) => p.portNumber)
    .filter(Boolean)
    .sort();
  const portOptions = destPorts
    .map((p) => p.portNumber)
    .filter(Boolean)
    .sort();
  const providerOptions = providers.map((p) => p.name).sort();

  const handleCreate = async () => {
    try {
      setIsSaving(true);
      setErrorMessage(null);

      // Build payload using values from inputs (which come from selected states)
      const payload: any = {
        subId: formData.subId || "",
        capacityMbps: formData.capacityMbps || 0,
        connectionType: formData.connectionType || "",
        // Source Information - use values from selected states (same as what's shown in inputs)
        datacenterIdSource:
          selectedSourceDatacenter?.datacenterId ||
          formData.datacenterIdSource ||
          "",
        hardwareIdSource:
          selectedSourceHardwareDetails?.id ||
          formData.hardwareIdSource ||
          null,
        portIdSource:
          selectedSourcePortDetails?.id || formData.portIdSource || null,
        // Metadata - always include as empty object
        metadata: {},
      };

      // Add optional destination fields if provided
      const destDatacenterId =
        selectedDestDatacenter?.datacenterId || formData.datacenterIdDest;
      const destHardwareId =
        selectedDestHardwareDetails?.id || formData.hardwareIdDest;
      const destPortId = selectedDestPortDetails?.id || formData.portIdDest;

      if (destDatacenterId) {
        payload.datacenterIdDest = destDatacenterId;
      }
      if (destHardwareId !== null && destHardwareId !== undefined) {
        payload.hardwareIdDest = destHardwareId;
      }
      if (destPortId !== null && destPortId !== undefined) {
        payload.portIdDest = destPortId;
      }

      await onCreate(payload);
    } catch (error: any) {
      console.error("Error creating connection:", error);
      const errorMsg =
        error?.message || "An error occurred while creating the connection";
      setErrorMessage(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const SOURCE_TABS = [
    { id: "datacenter", label: "Datacenter" },
    { id: "hardware", label: "Hardware" },
    { id: "port", label: "Port" },
  ];

  const DEST_TABS = [
    { id: "datacenter", label: "Datacenter" },
    { id: "hardware", label: "Hardware" },
    { id: "port", label: "Port" },
  ];

  return (
    <>
      {/* Error Alert Modal */}
      <GenericAlert
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        onConfirm={() => setErrorMessage(null)}
        title="Error Creating Connection"
        description={errorMessage || ""}
        variant="error"
        confirmLabel="OK"
        hideCancel
      />

      <GenericModal
        isOpen={isOpen}
        onClose={onClose}
        title="Create Datacenter Connection"
        customWidth="max-w-6xl"
      >
        <div className="space-y-6">
          {/* Connection Information */}
          <GenericCard isCollapsible={false} className="p-5">
            <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
              Connection Information
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <GenericInput
                label="Subscription ID"
                value={formData.subId || ""}
                onChange={(value) => handleInputChange("subId", value)}
              />
              <GenericInput
                label="Connection Type"
                value={formData.connectionType || ""}
                onChange={(value) => handleInputChange("connectionType", value)}
              />
              <GenericInput
                label="Capacity (Mbps)"
                value={
                  formData.capacityMbps ? String(formData.capacityMbps) : ""
                }
                onChange={(value) =>
                  handleInputChange("capacityMbps", Number(value) || 0)
                }
                type="number"
              />
            </div>
          </GenericCard>

          {/* Source Information */}
          <GenericCard
            isCollapsible={true}
            initialExpanded={true}
            headerContent={
              <h3 className="text-md font-semibold text-semantic-text-primary">
                Source Information
              </h3>
            }
            className="p-5"
          >
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-4">
              <nav className="flex space-x-4" aria-label="Tabs">
                {SOURCE_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() =>
                      setActiveSourceTab(
                        tab.id as "datacenter" | "hardware" | "port"
                      )
                    }
                    className={`
                      px-3 py-2 text-sm font-medium focus:outline-none rounded-t
                      ${
                        activeSourceTab === tab.id
                          ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary bg-semantic-bg-secondary"
                          : "text-gray-500 hover:text-gray-700"
                      }
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-4">
              {/* Datacenter Tab */}
              {activeSourceTab === "datacenter" && (
                <>
                  <div className="mb-4">
                    <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                      Source Datacenter
                    </label>
                    <GenericDropdown
                      items={sourceDataCenterOptions}
                      value={
                        isLoadingSourceDataCenters
                          ? "Loading..."
                          : selectedSourceDatacenterLabel
                      }
                      onSelect={handleSourceDatacenterSelect}
                      defaultLabel="Select Datacenter"
                      disabled={isLoadingSourceDataCenters}
                      minWidthClass="min-w-[300px]"
                      maxWidthClass="max-w-[400px]"
                      dropdownMaxHeightClass="max-h-48"
                      fixedPosition={true}
                    />
                  </div>

                  {/* Always show inputs (empty or with data) */}
                  <div className="relative">
                    {isLoadingSourceDatacenterDetails && (
                      <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                          <p className="text-sm text-gray-500">
                            Loading datacenter details...
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4">
                      <GenericInput
                        label="Datacenter ID"
                        value={selectedSourceDatacenter?.datacenterId || ""}
                        readOnly
                      />
                      <GenericInput
                        label="Display Name"
                        value={selectedSourceDatacenter?.displayName || ""}
                        readOnly
                      />
                      <GenericInput
                        label="Direction"
                        value={selectedSourceDatacenter?.direction || ""}
                        readOnly
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Hardware Tab */}
              {activeSourceTab === "hardware" && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col">
                      <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                        Source Datacenter
                      </label>
                      <GenericDropdown
                        items={sourceDataCenterOptions}
                        value={selectedSourceDatacenter?.displayName || ""}
                        onSelect={handleSourceDatacenterSelect}
                        defaultLabel="Select Datacenter"
                        disabled={false}
                        minWidthClass="min-w-[300px]"
                        maxWidthClass="max-w-[400px]"
                        dropdownMaxHeightClass="max-h-48"
                        fixedPosition={true}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                        Source Hardware
                      </label>
                      <GenericDropdown
                        items={sourceHardwareOptions}
                        value={selectedSourceHardwareLabel}
                        onSelect={handleSourceHardwareSelect}
                        defaultLabel="Select Hardware"
                        disabled={
                          !selectedSourceDatacenter?.datacenterId ||
                          isLoadingSourceHardwareList
                        }
                        minWidthClass="min-w-[300px]"
                        maxWidthClass="max-w-[400px]"
                        dropdownMaxHeightClass="max-h-48"
                        fixedPosition={true}
                      />
                    </div>
                  </div>

                  {/* Show message if no hardware available */}
                  {selectedSourceDatacenter?.datacenterId &&
                    !isLoadingSourceHardwareList &&
                    sourceHardwareList.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No Hardware available</p>
                      </div>
                    )}

                  {/* Always show inputs (empty or with data) */}
                  {!(
                    selectedSourceDatacenter?.datacenterId &&
                    !isLoadingSourceHardwareList &&
                    sourceHardwareList.length === 0
                  ) && (
                    <div className="relative">
                      {isLoadingSourceHardwareDetails && (
                        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                          <div className="flex flex-col items-center gap-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                            <p className="text-sm text-gray-500">
                              Loading hardware details...
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-4">
                        <GenericInput
                          label="Hostname"
                          value={selectedSourceHardwareDetails?.hostname || ""}
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Port Tab */}
              {activeSourceTab === "port" && (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="flex flex-col">
                      <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                        Source Datacenter
                      </label>
                      <GenericDropdown
                        items={sourceDataCenterOptions}
                        value={selectedSourceDatacenter?.displayName || ""}
                        onSelect={handleSourceDatacenterSelect}
                        defaultLabel="Select Datacenter"
                        disabled={isLoadingSourceDataCenters}
                        minWidthClass="min-w-[300px]"
                        maxWidthClass="max-w-[400px]"
                        dropdownMaxHeightClass="max-h-48"
                        fixedPosition={true}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                        Source Hardware
                      </label>
                      <GenericDropdown
                        items={sourceHardwareOptions}
                        value={selectedSourceHardwareLabel}
                        onSelect={handleSourceHardwareSelect}
                        defaultLabel="Select Hardware"
                        disabled={
                          !selectedSourceDatacenter?.datacenterId ||
                          isLoadingSourceHardwareList
                        }
                        minWidthClass="min-w-[300px]"
                        maxWidthClass="max-w-[400px]"
                        dropdownMaxHeightClass="max-h-48"
                        fixedPosition={true}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                        Source Port
                      </label>
                      <GenericDropdown
                        items={sourcePortOptions}
                        value={selectedSourcePortLabel}
                        onSelect={handleSourcePortSelect}
                        defaultLabel="Select Port"
                        disabled={
                          !selectedSourceHardwareDetails?.id ||
                          isLoadingSourcePortsList
                        }
                        minWidthClass="min-w-[300px]"
                        maxWidthClass="max-w-[400px]"
                        dropdownMaxHeightClass="max-h-48"
                        fixedPosition={true}
                      />
                    </div>
                  </div>

                  {/* Show message if no ports available */}
                  {selectedSourceHardwareDetails?.id &&
                    !isLoadingSourcePortsList &&
                    sourcePortsList.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No Port Available</p>
                      </div>
                    )}

                  {/* Always show inputs (empty or with data) */}
                  {!(
                    selectedSourceHardwareDetails?.id &&
                    !isLoadingSourcePortsList &&
                    sourcePortsList.length === 0
                  ) && (
                    <div className="relative">
                      {isLoadingSourcePortDetails && (
                        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                          <div className="flex flex-col items-center gap-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                            <p className="text-sm text-gray-500">
                              Loading port details...
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-4">
                        <GenericInput
                          label="Port Number"
                          value={selectedSourcePortDetails?.portNumber || ""}
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </GenericCard>

          {/* Destination Information */}
          <GenericCard
            isCollapsible={true}
            initialExpanded={true}
            headerContent={
              <h3 className="text-md font-semibold text-semantic-text-primary">
                Destination Information
              </h3>
            }
            className="p-5"
          >
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-4">
              <nav className="flex space-x-4" aria-label="Tabs">
                {DEST_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() =>
                      handleDestTabChange(
                        tab.id as "datacenter" | "hardware" | "port"
                      )
                    }
                    className={`
                      px-3 py-2 text-sm font-medium focus:outline-none rounded-t
                      ${
                        activeDestTab === tab.id
                          ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary bg-semantic-bg-secondary"
                          : "text-gray-500 hover:text-gray-700"
                      }
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            {(() => {
              // For datacenter tab, show dropdown and always show inputs
              if (activeDestTab === "datacenter") {
                return (
                  <div className="mt-4">
                    <div className="mb-4">
                      <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                        Destination Datacenter
                      </label>
                      <GenericDropdown
                        items={dataCenterOptions}
                        value={
                          isLoadingDataCenters
                            ? "Loading..."
                            : selectedDestDatacenterLabel
                        }
                        onSelect={handleDestDatacenterSelect}
                        defaultLabel="Select All"
                        disabled={isLoadingDataCenters}
                        minWidthClass="min-w-[300px]"
                        maxWidthClass="max-w-[400px]"
                        dropdownMaxHeightClass="max-h-48"
                        fixedPosition={true}
                      />
                    </div>

                    {/* Always show inputs (empty or with data) */}
                    <div className="relative">
                      {isLoadingDestDatacenterDetails && (
                        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                          <div className="flex flex-col items-center gap-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                            <p className="text-sm text-gray-500">
                              Loading datacenter details...
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-4">
                        <GenericInput
                          label="Datacenter ID"
                          value={selectedDestDatacenter?.datacenterId || ""}
                          readOnly
                        />
                        <GenericInput
                          label="Display Name"
                          value={selectedDestDatacenter?.displayName || ""}
                          readOnly
                        />
                        <GenericInput
                          label="Direction"
                          value={selectedDestDatacenter?.direction || ""}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              // For hardware tab, show dropdowns and always show inputs
              if (activeDestTab === "hardware") {
                const datacenterId =
                  selectedDestDatacenter?.datacenterId ||
                  formData.datacenterIdDest;
                const datacenterDisplayName =
                  selectedDestDatacenter?.displayName || "";

                return (
                  <div className="mt-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Destination Datacenter
                        </label>
                        <GenericDropdown
                          items={dataCenterOptions}
                          value={datacenterDisplayName || "Select All"}
                          onSelect={handleDestDatacenterSelect}
                          defaultLabel="Select All"
                          disabled={false}
                          minWidthClass="min-w-[300px]"
                          maxWidthClass="max-w-[400px]"
                          dropdownMaxHeightClass="max-h-48"
                          fixedPosition={true}
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Destination Hardware
                        </label>
                        <GenericDropdown
                          items={hardwareOptionsForDest}
                          value={selectedDestHardwareLabel}
                          onSelect={handleDestHardwareSelectForTab}
                          defaultLabel="Select Hardware"
                          disabled={!datacenterId || isLoadingHardwareList}
                          minWidthClass="min-w-[300px]"
                          maxWidthClass="max-w-[400px]"
                          dropdownMaxHeightClass="max-h-48"
                          fixedPosition={true}
                        />
                      </div>
                    </div>

                    {/* Show message if no hardware available */}
                    {datacenterId &&
                      !isLoadingHardwareList &&
                      hardwareListForDest.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-sm">No Hardware available</p>
                        </div>
                      )}

                    {/* Always show hardware inputs (empty or with data) */}
                    {!(
                      datacenterId &&
                      !isLoadingHardwareList &&
                      hardwareListForDest.length === 0
                    ) && (
                      <div className="relative">
                        {isLoadingHardwareDetails && (
                          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                            <div className="flex flex-col items-center gap-2">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                              <p className="text-sm text-gray-500">
                                Loading hardware details...
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                          <GenericInput
                            label="Hostname"
                            value={selectedDestHardwareDetails?.hostname || ""}
                            readOnly
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // For port tab, show dropdowns and always show inputs
              if (activeDestTab === "port") {
                const datacenterDisplayName =
                  selectedDestDatacenter?.displayName || "Select All";
                const hardwareLabel = selectedDestHardwareLabel || "Select All";
                const hardwareId =
                  selectedDestHardwareDetails?.id || formData.hardwareIdDest;
                const datacenterId =
                  selectedDestDatacenter?.datacenterId ||
                  formData.datacenterIdDest;

                return (
                  <div className="mt-4">
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Destination Datacenter
                        </label>
                        <GenericDropdown
                          items={dataCenterOptions}
                          value={datacenterDisplayName}
                          onSelect={handleDestDatacenterSelect}
                          defaultLabel="Select All"
                          disabled={isLoadingDataCenters}
                          minWidthClass="min-w-[300px]"
                          maxWidthClass="max-w-[400px]"
                          dropdownMaxHeightClass="max-h-48"
                          fixedPosition={true}
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Destination Hardware
                        </label>
                        <GenericDropdown
                          items={hardwareOptionsForDest}
                          value={hardwareLabel}
                          onSelect={handleDestHardwareSelectForTab}
                          defaultLabel="Select All"
                          disabled={!datacenterId || isLoadingHardwareList}
                          minWidthClass="min-w-[300px]"
                          maxWidthClass="max-w-[400px]"
                          dropdownMaxHeightClass="max-h-48"
                          fixedPosition={true}
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Destination Port
                        </label>
                        <GenericDropdown
                          items={portOptions}
                          value={selectedDestPortLabel}
                          onSelect={handleDestPortSelectForTab}
                          defaultLabel="Select Port"
                          disabled={!hardwareId || isLoadingDestPorts}
                          minWidthClass="min-w-[300px]"
                          maxWidthClass="max-w-[400px]"
                          dropdownMaxHeightClass="max-h-48"
                          fixedPosition={true}
                        />
                      </div>
                    </div>

                    {/* Show message if no ports available */}
                    {hardwareId &&
                      !isLoadingDestPorts &&
                      destPorts.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-sm">No Port Available</p>
                        </div>
                      )}

                    {/* Always show inputs (empty or with data) */}
                    {!(
                      hardwareId &&
                      !isLoadingDestPorts &&
                      destPorts.length === 0
                    ) && (
                      <div className="relative">
                        {isLoadingDestPorts && (
                          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                            <div className="flex flex-col items-center gap-2">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                              <p className="text-sm text-gray-500">
                                Loading port details...
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                          <GenericInput
                            label="Port Number"
                            value={
                              selectedDestPortDetails?.portNumber ||
                              selectedDestPortLabel ||
                              ""
                            }
                            readOnly
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return null;
            })()}
          </GenericCard>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <GenericButton
            label="Cancel"
            variant="secondary"
            onClick={onClose}
            disabled={isSaving}
          />
          <GenericButton
            label={isSaving ? "Creating..." : "Create"}
            variant="brand"
            onClick={handleCreate}
            disabled={isSaving}
          />
        </div>
      </GenericModal>
    </>
  );
};
